import React, { useEffect, useState, useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { Media } from '../../../models/Media/Media';
import { Pagination } from '../../../models/Media/contracts';
import styles from './media-library.module.scss';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import {
  FaEdit as EditIcon,
  FaTrash as DeleteIcon,
  FaSearch,
  FaAngleLeft,
  FaAngleRight
} from 'react-icons/fa';
import { SimpleModal } from '../../components/Modals/SimpleModal/SimpleModal';
import { EditMediaModal } from '../../components/Modals/EditMediaModal/EditMediaModal';
import { MediaUpload } from '../../components/MediaUpload/MediaUpload';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import { FullscreenGallery } from '../../components/FullscreenGallery/FullscreenGallery';
import { toast } from 'react-toastify';

// Pagination Range Hook (for generating page numbers)
const usePaginationRange = (
  currentPage: number,
  totalPages: number,
  siblingCount: number = 1
) => {
  return useMemo(() => {
    const totalPageNumbers = siblingCount + 5;
    if (totalPageNumbers >= totalPages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;
    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, '...', totalPages];
    }
    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = Array.from(
        { length: rightItemCount },
        (_, i) => totalPages - rightItemCount + i + 1
      );
      return [firstPageIndex, '...', ...rightRange];
    }
    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i
      );
      return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
    }
    return [];
  }, [currentPage, totalPages, siblingCount]);
};

function MediaLibraryPage() {
  const [mediaItems, setMediaItems] = useState<Media[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [mediaToEdit, setMediaToEdit] = useState<Media | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<number | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  // State for search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const paginationRange = usePaginationRange(
    currentPage,
    pagination?.last_page || 1
  );

  const fetchMedia = async (
    page: number = 1,
    search: string = '',
    limit: number = 20
  ) => {
    setIsLoading(true);
    try {
      const response = await Media.all(page, search, limit);
      setMediaItems(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Neuspelo učitavanje medija.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia(currentPage, debouncedSearchTerm, perPage);
  }, [currentPage, debouncedSearchTerm, perPage]);

  const handleUpdateComplete = (updatedMedia: Media) => {
    setMediaItems((currentItems) =>
      currentItems.map((item) =>
        item.id === updatedMedia.id ? updatedMedia : item
      )
    );
    setMediaToEdit(null);
    toast.success('Slika je uspešno izmenjena!');
  };

  const handleDeleteClick = (mediaId: number) => {
    setMediaToDelete(mediaId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!mediaToDelete) return;

    try {
      await Media.delete(mediaToDelete);
      toast.success('Slika je uspešno obrisana!');
      // Refresh the current page after deletion
      fetchMedia(currentPage, debouncedSearchTerm, perPage);
    } catch (error) {
      console.error('Failed to delete media item:', error);
      toast.error('Brisanje slike nije uspelo.');
    } finally {
      setShowDeleteConfirm(false);
      setMediaToDelete(null);
    }
  };

  const handleUploadComplete = () => {
    setIsUploadModalOpen(false);
    toast.success('Slika je uspešno dodata!');
    // Go back to the first page to see the new uploads
    if (currentPage === 1) {
      fetchMedia(1, '', perPage);
    } else {
      setCurrentPage(1);
    }
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to page 1
  };

  const handlePageChange = (page: number | string) => {
    if (typeof page === 'number') {
      setCurrentPage(page);
    }
  };

  return (
    <>
      <div className="cs-inner-container">
        <div className={styles.pageHeader}>
          <h1 className="cs-title">Biblioteka Slika</h1>
          <div className={styles.controls}>
            <div className={styles.searchWrapper}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Pretraži po nazivu..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className={styles.perPageSelector}>
              <label htmlFor="perPage">Prikaži po:</label>
              <select
                id="perPage"
                value={perPage}
                onChange={handlePerPageChange}
                className={styles.selectDropdown}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
            <ButtonPrimary onClick={() => setIsUploadModalOpen(true)}>
              Dodaj Novu sliku
            </ButtonPrimary>
          </div>
        </div>

        <div className={styles.grid}>
          {isLoading ? (
            <p>Loading...</p>
          ) : (
            mediaItems.map((item, index) => (
              <div key={item.id} className={styles.gridItem}>
                <div className={styles.itemName}>{item.name}</div>
                <img
                  src={item.fullUrl}
                  alt={item.name}
                  className={styles.image}
                  onClick={() => setSelectedImageIndex(index)}
                  style={{ cursor: 'pointer' }}
                />
                <div className={styles.actions}>
                  <button
                    className="cs-edit-button"
                    onClick={() => setMediaToEdit(item)}
                    title="Izmeni"
                  >
                    <EditIcon />
                  </button>
                  <button
                    className="cs-delete-button"
                    onClick={() => handleDeleteClick(item.id)}
                    title="Obriši"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {pagination && pagination.total > 0 && !isLoading && (
          <div className={styles.pagination}>
            <button
              className={styles.pageArrow}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <FaAngleLeft /> Prethodna
            </button>
            {paginationRange.map((pageNumber, index) => {
              if (pageNumber === '...') {
                return (
                  <span
                    key={`${pageNumber}-${index}`}
                    className={styles.ellipsis}
                  >
                    ...
                  </span>
                );
              }
              return (
                <button
                  key={pageNumber}
                  className={`${styles.pageItem} ${
                    pageNumber === currentPage ? styles.active : ''
                  }`}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
            <button
              className={styles.pageArrow}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= pagination.last_page}
            >
              Sledeća <FaAngleRight />
            </button>
          </div>
        )}
      </div>

      <ConfirmationModal
        show={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Potvrda brisanja"
        message="Da li ste sigurni da želite trajno da obrišete ovaj medijski fajl?"
      />

      <SimpleModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Upload New Media"
      >
        <MediaUpload onUploadComplete={handleUploadComplete} />
      </SimpleModal>

      <EditMediaModal
        media={mediaToEdit}
        onClose={() => setMediaToEdit(null)}
        onUpdate={handleUpdateComplete}
      />

      {selectedImageIndex !== null && (
        <FullscreenGallery
          images={mediaItems}
          initialIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
        />
      )}
    </>
  );
}

export default MediaLibraryPage;
